import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Business as BusinessIcon, Link as LinkIcon } from "@mui/icons-material";
import { getContactConfig } from "../../../utils/contactConfig";
import { BORDER_RADIUS_SM } from "../../../theme/theme";
import RelationshipTypeSelect from "./RelationshipTypeSelect";
import { ClientLinkState, initialClientLinkState } from "./useClientLink";

const ENTITY_TYPES = ["Individual", "SoleTrader", "Company", "Family", "Trust"];

interface ClientLinkSectionProps {
  clientLink: ClientLinkState;
  setClientLink: React.Dispatch<React.SetStateAction<ClientLinkState>>;
  allClients: any[];
  loadingClients: boolean;
  onLoadClients: () => void;
}

const ClientLinkSection: React.FC<ClientLinkSectionProps> = ({
  clientLink,
  setClientLink,
  allClients,
  loadingClients,
  onLoadClients,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        p: 2,
        borderRadius: BORDER_RADIUS_SM,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        color="primary.main"
        sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}
      >
        <BusinessIcon fontSize="small" />
        Link to Client Entity (Optional)
      </Typography>

      {/* Mode toggle — clicking the active button resets to "none" */}
      <Box sx={{ display: "flex", gap: 1, mb: clientLink.mode !== "none" ? 1.5 : 0 }}>
        <Button
          size="small"
          variant={clientLink.mode === "new" ? "contained" : "outlined"}
          startIcon={<AddIcon sx={{ fontSize: "0.9rem !important" }} />}
          onClick={() =>
            setClientLink(
              clientLink.mode === "new"
                ? initialClientLinkState
                : { ...initialClientLinkState, mode: "new" }
            )
          }
          sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
        >
          New Client
        </Button>
        <Button
          size="small"
          variant={clientLink.mode === "existing" ? "contained" : "outlined"}
          startIcon={<LinkIcon sx={{ fontSize: "0.9rem !important" }} />}
          onClick={() => {
            if (clientLink.mode === "existing") {
              setClientLink(initialClientLinkState);
            } else {
              onLoadClients();
              setClientLink({ ...initialClientLinkState, mode: "existing" });
            }
          }}
          sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
        >
          Link Existing
        </Button>
      </Box>

      {clientLink.mode === "new" && (
        <Grid container spacing={1.25}>
          <Grid item xs={8}>
            <TextField
              required
              label="Client Name"
              fullWidth
              size="small"
              sx={{ bgcolor: "background.paper" }}
              value={clientLink.newClientName}
              onChange={(e) =>
                setClientLink((prev) => ({ ...prev, newClientName: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Type</InputLabel>
              <Select
                value={clientLink.newClientEntityType}
                label="Type"
                sx={{ bgcolor: "background.paper" }}
                onChange={(e) =>
                  setClientLink((prev) => ({
                    ...prev,
                    newClientEntityType: e.target.value,
                    relationshipType: getContactConfig(e.target.value).defaultRelationship,
                  }))
                }
              >
                {ENTITY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <RelationshipTypeSelect
              value={clientLink.relationshipType}
              options={getContactConfig(clientLink.newClientEntityType).relationships}
              onChange={(value) => setClientLink((prev) => ({ ...prev, relationshipType: value }))}
              size="small"
              sx={{ bgcolor: "background.paper" }}
            />
          </Grid>
        </Grid>
      )}

      {clientLink.mode === "existing" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Autocomplete
            options={allClients}
            loading={loadingClients}
            getOptionLabel={(opt) => opt.name || ""}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            value={clientLink.selectedClient}
            onChange={(_, val) =>
              setClientLink((prev) => ({
                ...prev,
                selectedClient: val,
                relationshipType: val
                  ? getContactConfig(val.entityType).defaultRelationship
                  : "Other",
              }))
            }
            filterOptions={(opts, { inputValue }) => {
              if (!inputValue) return opts;
              const q = inputValue.toLowerCase();
              return opts.filter(
                (o) =>
                  o.name?.toLowerCase().includes(q) ||
                  o.email?.toLowerCase().includes(q)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search clients"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt._id}>
                <Box>
                  <Typography variant="body2">{opt.name}</Typography>
                  {opt.entityType && (
                    <Typography variant="caption" color="text.secondary">
                      {opt.entityType}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          />
          {clientLink.selectedClient && (
            <RelationshipTypeSelect
              value={clientLink.relationshipType}
              options={getContactConfig(clientLink.selectedClient?.entityType).relationships}
              onChange={(value) => setClientLink((prev) => ({ ...prev, relationshipType: value }))}
              size="small"
              sx={{ bgcolor: "background.paper" }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClientLinkSection;
